import * as THREE from 'three';
import type { Vec3 } from '../../sim/mathx';
import type { TargetClass } from '../../sim/targetTypes';
import { setThreePositionFromEnu } from './ScoutView';

export const TARGET_TRAIL_SECONDS = 5;
export const TARGET_TRAIL_CAPACITY = 128;

const materialForClass = (targetClass: TargetClass): THREE.MeshStandardMaterial => {
  const colors: Record<TargetClass, number> = {
    drone: 0x42a5f5,
    fixedwing: 0xffb74d,
    vehicle: 0x8bc34a,
    bird: 0xce93d8,
  };
  return new THREE.MeshStandardMaterial({
    color: colors[targetClass],
    roughness: 0.7,
    metalness: 0.05,
  });
};

const fixedWingGeometry = (): THREE.BufferGeometry => {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(
      [
        0.75, 0, 0, -0.55, 0.48, 0, -0.35, 0, 0.14, -0.55, -0.48, 0, 0.75, 0, 0, -0.55, 0.48, 0,
        -0.35, 0, -0.1, -0.55, -0.48, 0,
      ],
      3,
    ),
  );
  geometry.setIndex([
    0, 1, 2, 0, 2, 3, 4, 6, 5, 4, 7, 6, 0, 4, 5, 0, 5, 1, 1, 5, 6, 1, 6, 2, 2, 6, 7, 2, 7, 3, 3, 7,
    4, 3, 4, 0,
  ]);
  geometry.computeVertexNormals();
  return geometry;
};

const meshForClass = (targetClass: TargetClass): THREE.Mesh => {
  let geometry: THREE.BufferGeometry;
  switch (targetClass) {
    case 'drone':
      geometry = new THREE.BoxGeometry(0.5, 0.5, 0.13);
      break;
    case 'fixedwing':
      geometry = fixedWingGeometry();
      break;
    case 'vehicle':
      geometry = new THREE.BoxGeometry(0.9, 0.45, 0.25);
      break;
    case 'bird':
      geometry = new THREE.ConeGeometry(0.13, 0.4, 5);
      geometry.rotateZ(-Math.PI / 2);
      break;
  }
  const mesh = new THREE.Mesh(geometry, materialForClass(targetClass));
  mesh.name = `target:${targetClass}:mesh`;
  mesh.userData.targetClass = targetClass;
  mesh.castShadow = true;
  return mesh;
};

/**
 * Procedural target with render-local history. Labels intentionally use a DOM
 * hook instead of a Sprite: consumers render `labelText` at `labelAnchor`'s
 * projected position, so construction remains canvas-free and works in Node.
 */
export class TargetView extends THREE.Group {
  readonly targetClass: TargetClass;
  readonly targetMesh: THREE.Mesh;
  readonly labelAnchor = new THREE.Object3D();
  readonly trailGeometry = new THREE.BufferGeometry();
  readonly trail: THREE.Line;

  labelText: string;

  private readonly trailPositions = new Float32Array(TARGET_TRAIL_CAPACITY * 3);
  private readonly trailTimes = new Float64Array(TARGET_TRAIL_CAPACITY);
  private trailStart = 0;
  private trailCount = 0;
  private lastTrailTime = Number.NEGATIVE_INFINITY;
  private disposed = false;

  constructor(id: string, targetClass: TargetClass) {
    super();
    this.targetClass = targetClass;
    this.labelText = id;
    this.name = `target:${id}`;

    this.targetMesh = meshForClass(targetClass);
    this.add(this.targetMesh);

    this.labelAnchor.name = 'target:label-anchor';
    this.labelAnchor.position.z = targetClass === 'vehicle' ? 0.45 : 0.55;
    this.add(this.labelAnchor);

    const renderedTrailPositions = new Float32Array(TARGET_TRAIL_CAPACITY * 3);
    const positionAttribute = new THREE.BufferAttribute(renderedTrailPositions, 3);
    positionAttribute.setUsage(THREE.DynamicDrawUsage);
    this.trailGeometry.setAttribute('position', positionAttribute);
    this.trailGeometry.setDrawRange(0, 0);
    this.trail = new THREE.Line(
      this.trailGeometry,
      new THREE.LineBasicMaterial({ color: 0x88ccee, transparent: true, opacity: 0.35 }),
    );
    this.trail.name = 'target:trail';
    this.trail.frustumCulled = false;
    this.add(this.trail);
  }

  get entityId(): string {
    return this.labelText;
  }

  get trailPointCount(): number {
    return this.trailCount;
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  setPosition(posEnu: Vec3): void {
    setThreePositionFromEnu(this, posEnu);
  }

  update(posEnu: Vec3, timeSeconds: number): void {
    if (timeSeconds < this.lastTrailTime) this.clearTrail();
    this.setPosition(posEnu);
    this.pushTrailPosition(posEnu, timeSeconds);
    this.pruneTrail(timeSeconds);
    this.writeTrailGeometry(posEnu);
    this.lastTrailTime = timeSeconds;
  }

  reuse(id: string): void {
    if (this.disposed) throw new Error('Cannot reuse a disposed TargetView');
    this.labelText = id;
    this.name = `target:${id}`;
    this.visible = true;
    this.clearTrail();
  }

  prepareForPool(): void {
    this.removeFromParent();
    this.visible = false;
    this.clearTrail();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    this.targetMesh.geometry.dispose();
    const targetMaterials = Array.isArray(this.targetMesh.material)
      ? this.targetMesh.material
      : [this.targetMesh.material];
    for (const material of targetMaterials) material.dispose();
    this.trailGeometry.dispose();
    const trailMaterials = Array.isArray(this.trail.material)
      ? this.trail.material
      : [this.trail.material];
    for (const material of trailMaterials) material.dispose();
    this.clear();
  }

  private pushTrailPosition(posEnu: Vec3, timeSeconds: number): void {
    let index: number;
    if (this.trailCount < TARGET_TRAIL_CAPACITY) {
      index = (this.trailStart + this.trailCount) % TARGET_TRAIL_CAPACITY;
      this.trailCount += 1;
    } else {
      index = this.trailStart;
      this.trailStart = (this.trailStart + 1) % TARGET_TRAIL_CAPACITY;
    }
    const offset = index * 3;
    this.trailPositions[offset] = posEnu.x;
    this.trailPositions[offset + 1] = posEnu.y;
    this.trailPositions[offset + 2] = posEnu.z;
    this.trailTimes[index] = timeSeconds;
  }

  private pruneTrail(timeSeconds: number): void {
    while (
      this.trailCount > 0 &&
      timeSeconds - this.trailTimes[this.trailStart]! > TARGET_TRAIL_SECONDS
    ) {
      this.trailStart = (this.trailStart + 1) % TARGET_TRAIL_CAPACITY;
      this.trailCount -= 1;
    }
  }

  private writeTrailGeometry(currentPosition: Vec3): void {
    const attribute = this.trailGeometry.getAttribute('position') as THREE.BufferAttribute;
    const output = attribute.array as Float32Array;
    for (let outputIndex = 0; outputIndex < this.trailCount; outputIndex += 1) {
      const historyIndex = (this.trailStart + outputIndex) % TARGET_TRAIL_CAPACITY;
      const sourceOffset = historyIndex * 3;
      const outputOffset = outputIndex * 3;
      // The line is a child of the moving target, so write world history as
      // offsets from the current root position.
      output[outputOffset] = this.trailPositions[sourceOffset]! - currentPosition.x;
      output[outputOffset + 1] = this.trailPositions[sourceOffset + 1]! - currentPosition.y;
      output[outputOffset + 2] = this.trailPositions[sourceOffset + 2]! - currentPosition.z;
    }
    attribute.needsUpdate = true;
    this.trailGeometry.setDrawRange(0, this.trailCount);
  }

  private clearTrail(): void {
    this.trailStart = 0;
    this.trailCount = 0;
    this.lastTrailTime = Number.NEGATIVE_INFINITY;
    this.trailGeometry.setDrawRange(0, 0);
  }
}
